import React, { useCallback, useMemo } from "react";
import Alert from "components/ui/Alert/Alert";
import ChartsSection from "./components/ChartsSection";
import StatsGrid from "./components/StatsGrid";
import type { ChartSeries } from "./dashboardData";
import styles from "./DashboardPage.module.scss";
import { mapStatsToCards } from "./dashboardUtils";
import { useDashboardOverview } from "./useDashboardOverview";
import { useI18n } from "context/I18nContext";
import type { DashboardStat } from "utils/dashboardApi";

const EMPTY_SERIES: ChartSeries[] = [];

function DashboardPage() {
  const { t } = useI18n();
  const overviewParams = useMemo(
    () => ({
      range: "6m",
      groupBy: "month" as const,
    }),
    []
  );

  const {
    data,
    loading,
    statsLoading,
    chartsLoading,
    statsError,
    chartsError,
  } = useDashboardOverview(overviewParams);

  const translateStatLabel = useCallback(
    (stat: DashboardStat) => {
      const key = `dashboardPage.stats.labels.${stat.id}`;
      const translated = t(key);
      return translated === key ? stat.label : translated;
    },
    [t]
  );

  const statCards = useMemo(
    () => (data ? mapStatsToCards(data.stats, translateStatLabel) : []),
    [data, translateStatLabel]
  );

  const newCardsSeries = data?.charts.newCards?.series ?? EMPTY_SERIES;
  const waterDispenseSeries = data?.charts.waterDispense?.series ?? EMPTY_SERIES;
  const rechargeSeries = data?.charts.rechargeAmount?.series ?? EMPTY_SERIES;

  const resolvedError = statsError ?? chartsError;

  return (
    <div className={styles.page}>
      {resolvedError && (
        <Alert variant="error" title={t("dashboardPage.error")}>
          {resolvedError.message || t("dashboardPage.error")}
        </Alert>
      )}

      <StatsGrid items={statCards} loading={statsLoading || loading} />

      <ChartsSection
        loading={chartsLoading || loading}
        newCardsSeries={newCardsSeries}
        dispenseSeries={waterDispenseSeries}
        rechargeSeries={rechargeSeries}
      />
    </div>
  );
}

export default DashboardPage;
